import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useAsyncResource, UnauthorizedError } from '../composables/useAsyncResource'
import { apiMutate } from '../composables/apiMutate'

// Admin-only store for managing `ApiToken` rows that authenticate the
// `brain` CLI, the Discord bot, and any other bearer-auth client.
//
// The model exposes the cleartext value exactly once — on the in-memory
// record returned by `create_for!`. The API mirrors that: `POST` returns
// the raw value alongside the digest-only metadata; every subsequent
// read returns metadata only. The store keeps `lastCreatedRaw` in
// memory so the reveal modal can copy it once, then drops it the
// moment the modal closes (`forgetLastCreatedRaw`).

export interface ApiTokenRow {
  id: number
  name: string
  owner_email: string
  last_used_at: string | null
  created_at: string
}

interface CreatedToken extends ApiTokenRow {
  raw: string
}

export const useApiTokensStore = defineStore('apiTokens', () => {
  const lastCreatedRaw = ref<string | null>(null)

  const {
    data: tokens,
    loaded,
    error: loadError,
    refresh: fetchAll,
  } = useAsyncResource<ApiTokenRow[]>(
    async () => {
      const res = await fetch('/api/v1/api_tokens', { credentials: 'include' })
      if (res.status === 401) throw new UnauthorizedError()
      if (!res.ok) throw new Error(`Failed to load API tokens: ${res.status}`)
      return res.json()
    },
    { initialValue: [] },
  )

  async function create(name: string): Promise<CreatedToken> {
    const created = await apiMutate<CreatedToken>('/api/v1/api_tokens', {
      method: 'POST',
      body: { api_token: { name } },
      failureLabel: 'failed to create API token',
    })
    // Splice into the list as a digest-only row; keep raw separate so
    // the reveal modal can pick it up and the list view never has it.
    const { raw, ...metadata } = created
    tokens.value = [...tokens.value, metadata]
    lastCreatedRaw.value = raw
    return created
  }

  async function destroy(id: number) {
    await apiMutate(`/api/v1/api_tokens/${id}`, {
      method: 'DELETE',
      failureLabel: 'failed to revoke API token',
    })
    tokens.value = tokens.value.filter((t) => t.id !== id)
  }

  function forgetLastCreatedRaw() {
    lastCreatedRaw.value = null
  }

  return {
    tokens,
    loaded,
    loadError,
    lastCreatedRaw,
    fetchAll,
    create,
    destroy,
    forgetLastCreatedRaw,
  }
})
